// src/pages/Users/News/EditNewsModal.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import {
  FaCheck,
  FaCloudUploadAlt,
  FaEye,
  FaEyeSlash,
  FaImage,
  FaSave,
  FaTimes,
  FaUpload,
} from "react-icons/fa";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import { updateNews } from "../../../services/newsServices";
import {
  uploadNewsPhoto,
  toAbsolutePhotoUrl,
} from "../../../services/newsPhotoServices";

const FALLBACK = "/img/no-image.jpg";

const quillModules = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "clean"],
  ],
};

const quillFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "link",
  "clean",
];

const withBust = (url, version) =>
  url ? `${url}${url.includes("?") ? "&" : "?"}v=${version}` : url;

export default function EditNewsModal({ isOpen, onClose, refreshList, news }) {
  const initial = useMemo(
    () => ({
      id: news?.id ?? "",
      title: news?.title ?? "",
      content: news?.content ?? "",
      image_url: news?.image_url ?? "",
      is_active: !!news?.is_active,
    }),
    [news]
  );

  const [formData, setFormData] = useState(initial);
  const [saving, setSaving] = useState(false);

  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [outputFormat, setOutputFormat] = useState("webp");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [imgVersion, setImgVersion] = useState(0);

  const titleRef = useRef(null);

  const stateRef = useRef({
    formData: initial,
    saving: false,
    uploading: false,
  });

  useEffect(() => {
    stateRef.current = {
      formData,
      saving,
      uploading,
    };
  }, [formData, saving, uploading]);

  useEffect(() => {
    if (!isOpen) return;

    setFormData(initial);
    setSaving(false);
    setIsUploadOpen(false);
    setSelectedFile(null);
    setOutputFormat("webp");
    setUploading(false);
    setUploadProgress(0);
    setImgVersion((v) => v + 1);
  }, [isOpen, initial]);

  const isDirty = useMemo(() => {
    return (
      formData.title !== initial.title ||
      formData.content !== initial.content ||
      formData.image_url !== initial.image_url ||
      formData.is_active !== initial.is_active
    );
  }, [formData, initial]);

  const isDirtyRef = useRef(false);

  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  const handleClose = useCallback(() => {
    const latest = stateRef.current;

    if (latest.saving || latest.uploading) return;

    if (
      !isDirtyRef.current ||
      window.confirm("Des modifications ne sont pas enregistrées. Fermer ?")
    ) {
      onClose?.();
    }
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;

    const root = document.documentElement;
    const previousOverflow = root.style.overflow;
    root.style.overflow = "hidden";

    const onKey = (event) => {
      if (event.key !== "Escape") return;

      if (isUploadOpen) {
        setIsUploadOpen(false);
        return;
      }

      handleClose();
    };

    window.addEventListener("keydown", onKey);

    const timer = setTimeout(() => {
      titleRef.current?.focus();
    }, 100);

    return () => {
      root.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKey);
      clearTimeout(timer);
    };
  }, [isOpen, isUploadOpen, handleClose]);

  if (!isOpen) return null;

  const coverAbs = toAbsolutePhotoUrl(
    formData.image_url?.trim() ? formData.image_url : FALLBACK
  );

  const cover = withBust(coverAbs, imgVersion);

  const handleChange = (event) => {
    const { name, type, checked, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleContentChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      content: value,
    }));
  };

  const toggleVisibility = () => {
    setFormData((prev) => ({
      ...prev,
      is_active: !prev.is_active,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const title = formData.title.trim();

    if (!title) {
      toast.warn("Le titre est requis.");
      titleRef.current?.focus();
      return;
    }

    if (!formData.id) {
      toast.error("Identifiant de l’actualité manquant.");
      return;
    }

    try {
      setSaving(true);

      await updateNews({
        id: formData.id,
        title,
        content: formData.content,
        image_url: formData.image_url,
        is_active: formData.is_active ? 1 : 0,
      });

      toast.success("Actualité mise à jour.");
      await refreshList?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur updateNews:", error);
      toast.error("Erreur lors de la mise à jour de l’actualité.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (event) => {
    event.preventDefault();

    if (!formData.id) {
      toast.error("Impossible d’associer l’image : id manquant.");
      return;
    }

    if (!selectedFile) {
      toast.warn("Sélectionnez un fichier image.");
      return;
    }

    const okType = /^image\/(png|jpe?g|webp)$/i.test(selectedFile.type);
    if (!okType) {
      toast.error("Format d'image non supporté. Utilisez PNG / JPG / WEBP.");
      return;
    }

    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 10 Mo).");
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      const updated = await uploadNewsPhoto(formData.id, selectedFile, {
        format: outputFormat,
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          const progress = Math.round((evt.loaded / evt.total) * 100);
          setUploadProgress(progress);
        },
      });

      setFormData((prev) => ({
        ...prev,
        image_url: updated?.image_url || prev.image_url,
      }));

      setImgVersion((v) => v + 1);
      setIsUploadOpen(false);
      setSelectedFile(null);
      setUploadProgress(0);

      toast.success("Image téléversée et optimisée.");
    } catch (error) {
      console.error("Upload image news:", error);
      toast.error("Erreur lors du téléversement de l’image.");
    } finally {
      setUploading(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-slate-950/70 p-3 backdrop-blur-sm sm:flex sm:items-center sm:justify-center sm:p-6"
      onClick={(event) => {
        if (event.target === event.currentTarget) handleClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Modifier une actualité"
    >
      <article
        className="absolute inset-x-0 bottom-0 flex max-h-[94dvh] flex-col overflow-hidden rounded-t-[2rem] bg-white shadow-2xl ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800 sm:relative sm:inset-auto sm:w-full sm:max-w-5xl sm:rounded-[2rem]"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="relative overflow-hidden bg-gradient-to-r from-slate-950 via-blue-900 to-violet-800 px-5 py-5 text-white sm:px-6">
          <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-white/15 blur-3xl" />

          <div className="relative flex items-start justify-between gap-4">
            <div className="min-w-0">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] ring-1 ring-white/20">
                <FaImage />
                Modification
              </span>

              <h2 className="mt-3 text-2xl font-bold leading-tight">
                Modifier l’actualité
              </h2>

              <p className="mt-1 line-clamp-2 text-sm leading-6 text-white/80">
                {news?.title || "Actualité du club"}
              </p>
            </div>

            <button
              type="button"
              onClick={handleClose}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white backdrop-blur transition hover:bg-white/25 active:scale-95"
              aria-label="Fermer"
              title="Fermer"
            >
              <FaTimes />
            </button>
          </div>
        </header>

        <form
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto p-5 sm:p-6"
        >
          <div className="grid gap-5 lg:grid-cols-[0.95fr_1.05fr]">
            <section className="space-y-4">
              <div className="overflow-hidden rounded-[1.5rem] border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950">
                <div className="aspect-[16/10] w-full bg-slate-100 dark:bg-slate-900">
                  <img
                    src={cover}
                    alt="Illustration actualité"
                    onError={(event) => {
                      event.currentTarget.src = FALLBACK;
                    }}
                    className="h-full w-full object-contain"
                  />
                </div>

                <div className="border-t border-slate-200 p-4 dark:border-slate-800">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-bold text-slate-950 dark:text-white">
                        Image de l’article
                      </p>
                      <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
                        Remplace l’image associée à l’actualité.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => setIsUploadOpen(true)}
                      className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 text-sm font-bold text-white transition active:scale-95 dark:bg-white dark:text-slate-950"
                    >
                      <FaUpload />
                      Téléverser
                    </button>
                  </div>
                </div>
              </div>

              <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                  URL de l’image
                </p>

                <input
                  type="text"
                  name="image_url"
                  value={formData.image_url}
                  onChange={handleChange}
                  readOnly
                  placeholder="Remplie automatiquement après upload"
                  className="mt-2 h-11 w-full rounded-2xl border border-slate-200 bg-white px-3 text-xs text-slate-700 outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                />
              </div>

              <div className="rounded-[1.5rem] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-bold text-slate-950 dark:text-white">
                      Visibilité de l’actualité
                    </p>
                    <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
                      Une actualité visible apparaît dans le fil des adhérents.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={toggleVisibility}
                    className={`relative inline-flex h-8 w-[62px] shrink-0 items-center rounded-full p-1 transition ${
                      formData.is_active
                        ? "bg-emerald-500"
                        : "bg-slate-300 dark:bg-slate-700"
                    }`}
                    aria-pressed={formData.is_active}
                    aria-label="Basculer la visibilité"
                  >
                    <span
                      className={`flex h-6 w-6 items-center justify-center rounded-full bg-white text-[11px] shadow transition ${
                        formData.is_active
                          ? "translate-x-[30px]"
                          : "translate-x-0"
                      }`}
                    >
                      {formData.is_active ? (
                        <FaEye className="text-emerald-600" />
                      ) : (
                        <FaEyeSlash className="text-slate-500" />
                      )}
                    </span>
                  </button>
                </div>

                <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-bold text-slate-700 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700">
                  {formData.is_active ? (
                    <>
                      <FaCheck className="text-emerald-500" />
                      Actualité visible
                    </>
                  ) : (
                    <>
                      <FaEyeSlash className="text-slate-400" />
                      Actualité masquée
                    </>
                  )}
                </div>
              </div>
            </section>

            <section className="space-y-5">
              <div>
                <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                  Titre *
                </label>

                <input
                  ref={titleRef}
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Ex. : Résultats tournoi régional"
                  maxLength={160}
                  required
                  className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm font-semibold text-slate-900 outline-none transition focus:border-orange-400 focus:bg-white focus:ring-4 focus:ring-orange-400/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />

                <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                  {formData.title.length}/160 caractères
                </p>
              </div>

              <div>
                <label className="mb-1.5 block text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                  Contenu
                </label>

                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950">
                  <ReactQuill
                    theme="snow"
                    value={formData.content}
                    onChange={handleContentChange}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder="Rédigez ou mettez à jour l’actualité..."
                    className="bg-white text-slate-900 dark:bg-slate-950 dark:text-white"
                  />
                </div>
              </div>
            </section>
          </div>

          <footer className="sticky bottom-0 -mx-5 mt-6 border-t border-slate-200 bg-white/95 px-5 py-4 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95 sm:-mx-6 sm:px-6">
            <div className="grid grid-cols-2 gap-3 sm:flex sm:justify-end">
              <button
                type="button"
                onClick={handleClose}
                disabled={saving || uploading}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-slate-100 px-5 text-sm font-bold text-slate-700 transition active:scale-95 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-slate-800 dark:text-slate-200"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                disabled={saving || uploading}
                className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-slate-950 px-5 text-sm font-bold text-white transition active:scale-95 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white dark:text-slate-950"
              >
                <FaSave />
                {saving ? "Enregistrement..." : "Enregistrer"}
              </button>
            </div>

            <p className="mt-3 text-center text-[11px] text-slate-500 dark:text-slate-400 sm:text-right">
              {uploading
                ? "Téléversement de l’image en cours..."
                : saving
                ? "Sauvegarde de l’actualité en cours..."
                : isDirty
                ? "Modifications non enregistrées."
                : "Aucune modification à enregistrer."}
            </p>
          </footer>
        </form>
      </article>

      {isUploadOpen && (
        <UploadNewsImageModal
          newsId={formData.id}
          selectedFile={selectedFile}
          setSelectedFile={setSelectedFile}
          outputFormat={outputFormat}
          setOutputFormat={setOutputFormat}
          uploading={uploading}
          uploadProgress={uploadProgress}
          onClose={() => setIsUploadOpen(false)}
          onUpload={handleUpload}
        />
      )}
    </div>,
    document.body
  );
}

function UploadNewsImageModal({
  newsId,
  selectedFile,
  setSelectedFile,
  outputFormat,
  setOutputFormat,
  uploading,
  uploadProgress,
  onClose,
  onUpload,
}) {
  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[10000] bg-slate-950/70 p-3 backdrop-blur-sm sm:flex sm:items-center sm:justify-center sm:p-6"
      onClick={(event) => {
        if (event.target === event.currentTarget && !uploading) onClose?.();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Téléverser une image"
    >
      <form
        onSubmit={onUpload}
        className="w-full max-w-md overflow-hidden rounded-[2rem] bg-white shadow-2xl ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-800"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="bg-gradient-to-r from-slate-950 via-blue-900 to-violet-800 px-5 py-5 text-white">
          <h3 className="text-lg font-bold">Téléverser une image</h3>
          <p className="mt-1 text-sm text-white/80">
            Image optimisée et associée à l’actualité.
          </p>
        </div>

        <div className="space-y-4 p-5">
          {!newsId && (
            <p className="rounded-2xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-950/30 dark:text-red-300">
              Identifiant d’actualité manquant.
            </p>
          )}

          <label className="block">
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
              Fichier image
            </span>

            <label className="flex min-h-32 cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center transition hover:border-orange-300 dark:border-slate-700 dark:bg-slate-950">
              <FaCloudUploadAlt className="text-3xl text-slate-400" />

              <span className="mt-3 text-sm font-bold text-slate-800 dark:text-slate-100">
                {selectedFile ? selectedFile.name : "Choisir une image"}
              </span>

              <span className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                PNG, JPG ou WEBP — max 10 Mo
              </span>

              <input
                type="file"
                accept="image/*"
                className="hidden"
                disabled={uploading}
                onChange={(event) =>
                  setSelectedFile(event.target.files?.[0] || null)
                }
              />
            </label>
          </label>

          <label className="block">
            <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
              Format
            </span>

            <select
              value={outputFormat}
              onChange={(event) => setOutputFormat(event.target.value)}
              disabled={uploading}
              className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 px-3 text-sm font-bold text-slate-800 outline-none transition focus:border-orange-400 focus:bg-white focus:ring-4 focus:ring-orange-400/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
            >
              <option value="webp">WEBP recommandé</option>
              <option value="jpg">JPG</option>
              <option value="png">PNG</option>
            </select>
          </label>

          {uploading && (
            <div>
              <div className="h-3 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                <div
                  className="h-full rounded-full bg-orange-500 transition-all"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="mt-1 text-xs font-semibold text-slate-500 dark:text-slate-400">
                {uploadProgress}%
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={uploading}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-slate-100 px-4 text-sm font-bold text-slate-700 transition active:scale-95 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-slate-800 dark:text-slate-200"
            >
              <FaTimes />
              Fermer
            </button>

            <button
              type="submit"
              disabled={uploading || !selectedFile || !newsId}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 text-sm font-bold text-white transition active:scale-95 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white dark:text-slate-950"
            >
              <FaSave />
              {uploading ? "Transfert..." : "Sauvegarder"}
            </button>
          </div>
        </div>
      </form>
    </div>,
    document.body
  );
}